import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { ChatTemplateFooter } from "@arcadeai/design-system/components/ui/templates/chat-template";
import { AlertTriangle, Settings } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useChatModel, useChatRuntime, useChatToolkits } from "../context";
import {
  DEFAULT_CHAT_SUGGESTED_ACTIONS,
  type SuggestedAction,
} from "../default-suggested-actions";
import { ChatInput, type ChatInputProps } from "./chat-input";
import { ChatModelSelector } from "./chat-model-selector";
import { ChatSuggestions } from "./chat-suggestions";
import { ChatToolkitPickerAction } from "./chat-toolkit-picker-action";

export type ChatPromptProps = {
  suggestedActions?: SuggestedAction[];
  children?: ReactNode;
  /**
   * Render the toolkit picker as a leading input action. Turn off when the
   * consumer already renders a toolkit picker elsewhere (e.g. the dashboard's
   * chat header). Defaults to true so the standalone chat app still has one.
   */
  showToolkitPicker?: boolean;
} & Pick<ChatInputProps, "autoFocus" | "placeholder">;

export function ChatPrompt({
  suggestedActions = DEFAULT_CHAT_SUGGESTED_ACTIONS,
  children,
  showToolkitPicker = true,
  ...rest
}: ChatPromptProps) {
  const chat = useChatRuntime();
  const { isWaitingForToolkits, maxToolsForAI, selectedTools } =
    useChatToolkits();
  const { isConfigured } = useChatModel();
  const hasMessages = chat.messages.length > 0;
  const hasCappedSelection = selectedTools.length > maxToolsForAI;
  const [input, setInput] = useState("");
  const [prevChatId, setPrevChatId] = useState(chat.chatId);
  if (chat.chatId !== prevChatId) {
    setPrevChatId(chat.chatId);
    setInput("");
  }

  return (
    <ChatTemplateFooter>
      {children}
      {!hasMessages && isConfigured && (
        <ChatSuggestions
          actions={suggestedActions}
          isLoading={isWaitingForToolkits}
          onSelect={(action) => {
            chat.send(action.action);
            setInput("");
          }}
        />
      )}
      {!isConfigured && (
        <Alert
          className="border-destructive/40 bg-destructive/5 px-3 py-2 shadow-xs"
          variant="destructive"
        >
          <Settings className="size-4" />
          <AlertTitle>Chat is not configured</AlertTitle>
          <AlertDescription>
            <p>
              This workspace isn't set up to send messages yet. Contact your
              administrator.
            </p>
          </AlertDescription>
        </Alert>
      )}
      {hasCappedSelection && (
        <Alert
          className="border-warning/40 bg-warning/5 px-3 py-2 shadow-xs"
          variant="warning"
        >
          <AlertTriangle className="size-4" />
          <AlertTitle>Tool selection capped</AlertTitle>
          <AlertDescription>
            <p>
              Using{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {maxToolsForAI}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {selectedTools.length}
              </span>{" "}
              selected tools. Narrow the selection if you need a specific tool.
            </p>
          </AlertDescription>
        </Alert>
      )}
      <ChatInput
        {...rest}
        disabled={!isConfigured}
        inputLeadingAction={
          <div className="flex items-center gap-2">
            <ChatModelSelector />
            {showToolkitPicker && <ChatToolkitPickerAction />}
          </div>
        }
        isLoadingTools={isWaitingForToolkits}
        isStreaming={chat.isStreaming}
        onSend={chat.send}
        onStop={chat.stop}
        onValueChange={setInput}
        placeholder={
          isConfigured ? rest.placeholder : "Chat is not configured yet"
        }
        value={input}
      />
    </ChatTemplateFooter>
  );
}
